import { DEFAULT_ADDRESS_SYNC_CONFIG } from "../../../constants/addressSyncDefaults";
import { isObject } from "../../../utils";
import type { AddressSyncConfig, Answers, UpdateAnswerPayload } from "../types";

const trimValue = (value: string | number | boolean | string[]) => {
  if (!value) {
    return "";
  }
  // Array values are always pre-defined options
  if (Array.isArray(value)) {
    return value;
  }
  return value.toString().trim();
};

function syncAddresses(
  answers: Answers,
  syncMap: Record<string, string>,
): Answers {
  for (const [key, value] of Object.entries(syncMap)) {
    answers[value] = trimValue(answers[key]);
  }

  return answers;
}

function keepAddressesInSync(
  answers: Answers,
  config: AddressSyncConfig,
): Answers {
  const { linkQuestionName, linkQuestionValue = "yes", syncMap } = config;
  if (answers[linkQuestionName] === linkQuestionValue) {
    return syncAddresses(answers, syncMap);
  }

  return answers;
}

function clearLinkedAnswers(
  answers: Answers,
  syncMap: Record<string, string>,
): Answers {
  for (const key of Object.values(syncMap)) {
    delete answers[key];
  }

  return answers;
}

export const mergeAnswers = (
  answers: Answers,
  payload: UpdateAnswerPayload,
  addressSyncConfig: AddressSyncConfig = DEFAULT_ADDRESS_SYNC_CONFIG,
): Answers => {
  const { name, value } = payload;
  const {
    linkQuestionName,
    linkQuestionValue = "yes",
    syncMap,
  } = addressSyncConfig;

  // When `value` is an object, flatten it into the state
  if (isObject(value)) {
    return keepAddressesInSync(
      {
        ...answers,
        ...value,
      },
      addressSyncConfig,
    );
  }

  const updatedAnswers = payload
    ? { ...answers, [name]: trimValue(value) }
    : answers;

  if (name === linkQuestionName && value !== linkQuestionValue) {
    return clearLinkedAnswers(updatedAnswers, syncMap);
  }

  return keepAddressesInSync(updatedAnswers, addressSyncConfig);
};
